import React, { useEffect, useState } from 'react'
import { MdAdd, MdRemove } from 'react-icons/md'
import { lockBodyScroll } from '@/utils/bodyScrollLock'
import { formatOrdinalDate, getDayName } from '@/utils/deliveryDays'
import type { QuickAddOption } from './QuickAddEmptyDay'
import '@/styles/components/confirm-modal.css'

interface Props {
  isOpen: boolean
  onClose: () => void
  option: QuickAddOption | null
  /** The delivery day (0 = Sunday ... 6 = Saturday). */
  day: number
  /** Eligible start dates (YYYY-MM-DD), already in sequence. */
  dates: string[]
  /** The date to preselect; falls back to the earliest. */
  defaultDate?: string
  onConfirm: (quantity: number, startDate: string) => void | Promise<void>
}

/**
 * DF-343: the quick-add wizard. Step one picks the quantity, step two the start
 * date (preselecting the clicked day, or the earliest eligible), then it adds
 * the product to every delivery on that day.
 */
const QuickAddModal: React.FC<Props> = ({ isOpen, onClose, option, day, dates, defaultDate, onConfirm }) => {
  const [step, setStep] = useState<1 | 2>(1)
  const [quantity, setQuantity] = useState(1)
  const [selected, setSelected] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setStep(1)
    setQuantity(1)
    setSelected(defaultDate && dates.includes(defaultDate) ? defaultDate : dates[0] ?? '')
  }, [isOpen, dates, defaultDate])

  useEffect(() => {
    if (!isOpen) return
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose() }
    document.addEventListener('keydown', onEsc)
    const release = lockBodyScroll()
    return () => { document.removeEventListener('keydown', onEsc); release?.() }
  }, [isOpen, saving, onClose])

  if (!isOpen || !option) return null

  const dayName = getDayName(day)

  const handleConfirm = async () => {
    if (!selected || saving) return
    setSaving(true)
    try {
      await onConfirm(quantity, selected)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={e => { if (e.target === e.currentTarget && !saving) onClose() }}>
      <div className="modal-container confirm-modal" role="dialog" aria-modal="true" aria-labelledby="quickadd-title">
        <div className="modal-header">
          <h2 id="quickadd-title">Add to every {dayName}</h2>
        </div>

        <div className="modal-content">
          <p className="sun-quickadd-step">Step {step} of 2</p>

          <div className="sun-quickadd-product">
            {option.image
              ? <img className="sun-quickadd-image" src={option.image} alt="" />
              : <div className="sun-quickadd-image sun-quickadd-image--placeholder" aria-hidden="true" />}

            <div className="sun-quickadd-body">
              <span className="sun-quickadd-name">{option.name}</span>
              <span className="sun-quickadd-price">£{option.unitPrice.toFixed(2)} each</span>
            </div>

            {step === 1 ? (
              <div className="sun-quickadd-qty" role="group" aria-label={`Quantity for ${option.name}`}>
                <button
                  type="button"
                  className="sun-quickadd-qty-btn"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  <MdRemove aria-hidden="true" />
                </button>
                <span className="sun-quickadd-qty-value">{quantity}</span>
                <button
                  type="button"
                  className="sun-quickadd-qty-btn"
                  onClick={() => setQuantity(q => q + 1)}
                  aria-label="Increase quantity"
                >
                  <MdAdd aria-hidden="true" />
                </button>
              </div>
            ) : (
              <span className="sun-quickadd-qty-value">x {quantity}</span>
            )}
          </div>

          {step === 2 && (
            <label className="sun-quickadd-date-label">
              When would you like it to start?
              <select
                className="sun-quickadd-date-select"
                value={selected}
                onChange={e => setSelected(e.target.value)}
                disabled={saving}
              >
                {dates.map(d => (
                  <option key={d} value={d}>{formatOrdinalDate(d)}</option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="modal-footer">
          {step === 1 ? (
            <>
              <button className="btn-cancel" onClick={onClose}>Cancel</button>
              <button className="btn-confirm" onClick={() => setStep(2)}>Next</button>
            </>
          ) : (
            <>
              <button className="btn-cancel" onClick={() => setStep(1)} disabled={saving}>Back</button>
              <button className="btn-confirm" onClick={handleConfirm} disabled={saving || !selected}>
                {saving ? 'Adding...' : `Add to every ${dayName}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuickAddModal
